import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import TextField, { TextFieldVariants } from "@mui/material/TextField";
import { visuallyHidden } from "@mui/utils";
import { useState } from "react";
import Password, { InvalidPasswordError } from "users/auth/credentials/passwords/password";
import PasswordSchema from "users/auth/credentials/passwords/password-schema";

/**
 * Helper text is what makes a password field change height, because it appears and disappears as
 * the rules are met. The confirmation always carries a line of it, and the password carries a
 * checklist of every rule whether or not it is met, so the fields and everything under them stay
 * where the person left them. A blank line still has to be a non-empty string, or MUI drops the
 * element and takes its height with it.
 */
const reservedHelperTextLine = " ";

const PasswordInputContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: theme.spacing(1.5),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: 0,
  },
}));
// Both fields sit in a column of their own so the two share the row evenly, whatever the form
// around them is wide. Fixed widths were what left the rows of the invitation form ragged, each
// one a different length and none of them lining up with the next.
const PasswordColumn = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: theme.spacing(30),
}));
const InputField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  width: "100%",
}));
// The checklist belongs to the field above it, so that field gives up the gap under it and the
// list carries the space that separates the pair from whatever follows.
const PasswordField = styled(InputField)({
  marginBottom: 0,
});
const RequirementList = styled("ul")(({ theme }) => ({
  listStyle: "none",
  margin: 0,
  marginBottom: theme.spacing(1.5),
  padding: 0,
}));
/**
 * The colour is inherited rather than chosen, because this field renders on both surfaces the
 * product puts it on: a white card on the officer invitation, and a dark slate panel on the member
 * invitation. A palette colour that reads on one disappears into the other, and no single green
 * clears the contrast bar on both. So the state is carried by the icon and by weight, which work
 * on any background, and never by hue alone.
 */
const Requirement = styled("li", { shouldForwardProp: (prop) => prop !== "met" })<{ met: boolean }>(
  ({ theme, met }) => ({
    alignItems: "center",
    color: "inherit",
    display: "flex",
    fontSize: theme.typography.caption.fontSize,
    gap: theme.spacing(0.5),
    lineHeight: 1.66,
    opacity: met ? 1 : 0.65,
    "& .MuiSvgIcon-root": {
      fontSize: theme.typography.body2.fontSize,
    },
  }),
);
const RequirementState = styled("span")({ ...visuallyHidden });

type PasswordRequirementsProps = {
  id: string;
  password: string;
};

/**
 * Every rule, listed whether or not it is met. The icon and the colour both say which is which,
 * and the state is spelled out for anyone who gets neither.
 */
function PasswordRequirements(props: Readonly<PasswordRequirementsProps>) {
  const { id, password } = props;

  return (
    <RequirementList id={id} aria-label="Password requirements">
      {PasswordSchema.passwordRules.map((rule) => {
        const met = rule.isMet(password);

        return (
          <Requirement key={rule.requirement} met={met}>
            {met ? <CheckCircleIcon aria-hidden /> : <RadioButtonUncheckedIcon aria-hidden />}
            {rule.requirement}
            <RequirementState>{met ? " met" : " not met yet"}</RequirementState>
          </Requirement>
        );
      })}
    </RequirementList>
  );
}

export type PasswordIssues = {
  password: string[];
  confirmPassword: string[];
};

type PasswordInputProps = {
  idPrefix?: string;
  className?: string;
  variant?: TextFieldVariants;
  password?: Password;
  confirm?: boolean;
  onChange?: (password?: Password, error?: PasswordIssues) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

export default function PasswordInput(props: Readonly<PasswordInputProps>) {
  const { idPrefix, className, variant, password, confirm, onChange, onKeyDown } = props;

  const [fieldText, setFieldText] = useState<string>(password?.value ?? "");
  const [confirmFieldText, setConfirmFieldText] = useState<string>("");
  const [fieldIssues, setFieldIssues] = useState<PasswordIssues | undefined>();

  const [passwordIsDirty, setPasswordIsDirty] = useState<boolean>(false);
  const [confirmIsDirty, setConfirmIsDirty] = useState<boolean>(false);

  function buildPassword(passwordText: string, confirmPasswordText?: string): Password | undefined {
    try {
      return new Password(passwordText, confirmPasswordText);
    } catch (error) {
      if (error instanceof InvalidPasswordError) throw error;
    }
  }

  function handlePasswordChanged(passwordText: string, confirmPasswordText?: string) {
    let newPassword: Password | undefined;
    let newIssues: PasswordIssues | undefined;

    try {
      newPassword = buildPassword(passwordText, confirmPasswordText);
      setFieldIssues(undefined);
    } catch (error) {
      if (error instanceof InvalidPasswordError) {
        newIssues = { password: error.issues, confirmPassword: error.confirmationIssues };
        setFieldIssues(newIssues);
      }
    } finally {
      onChange?.(newPassword, newIssues);
    }
  }

  // A field only reports on itself once it has been typed in, so nothing is red on arrival.
  const passwordFailed = passwordIsDirty && (fieldIssues?.password.length ?? 0) > 0;
  const confirmIssue = confirmIsDirty ? fieldIssues?.confirmPassword[0] : undefined;

  const passwordId = idPrefix ? idPrefix + "-password-input" : "password-input";
  const requirementsId = passwordId + "-requirements";

  return (
    <PasswordInputContainer>
      <PasswordColumn>
        <PasswordField
          id={passwordId}
          className={className}
          label="New Password"
          onChange={(event) => {
            setPasswordIsDirty(true);
            setFieldText(event.target.value);
            handlePasswordChanged(event.target.value, confirm === true ? confirmFieldText : undefined);
          }}
          required
          type="password"
          autoComplete="new-password"
          value={fieldText}
          error={passwordFailed}
          variant={variant}
          slotProps={{ htmlInput: { "aria-describedby": requirementsId } }}
        />
        <PasswordRequirements id={requirementsId} password={fieldText} />
      </PasswordColumn>
      {confirm === true && (
        <PasswordColumn>
          <InputField
            id={idPrefix ? idPrefix + "-confirm-password-input" : "confirm-password-input"}
            className={className}
            label="Confirm Password"
            onChange={(event) => {
              setConfirmIsDirty(true);
              setConfirmFieldText(event.target.value);
              handlePasswordChanged(fieldText, event.target.value);
            }}
            required
            type="password"
            autoComplete="new-password"
            value={confirmFieldText}
            error={confirmIssue !== undefined}
            helperText={confirmIssue ?? reservedHelperTextLine}
            variant={variant}
            onKeyDown={onKeyDown}
          />
        </PasswordColumn>
      )}
    </PasswordInputContainer>
  );
}
